import React, { useEffect, useMemo } from "react";
import styled from "~/ui/styled";
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedStyle,
} from "~/ui/reanimated";
import { rules } from "~utils/validator"

interface Props {
  password: string;
  minLength?: number;
}

type Level = 0 | 1 | 2;

const COLORS = ["#fa5251", "#f0a224", "#07c160"];
const LABELS = ["弱", "中", "强"];
const WIDTHS = [0.33, 0.66, 1];
const PASSWORD_REGEX = rules.password.format.pattern

function calcLevel(pw: string): Level {
  if (!PASSWORD_REGEX.test(pw)) return 0; // 不符合正则 → 弱

  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const length = pw.length;

  // 强
  if (length >= 14 && hasLower && hasUpper) return 2;

  // 中
  if (length >= 10 || (hasLower && hasUpper)) return 1;

  return 0; // 默认弱
}

const PasswordStrengthAnimated: React.FC<Props> = ({ password, minLength = 6 }) => {
  const shouldShow = password.length >= minLength;

  const level = useMemo(
    () => (shouldShow ? calcLevel(password) : 0),
    [password, shouldShow]
  );

  const progress = useSharedValue(0);

  useEffect(() => {
    if (shouldShow) {
      progress.value = withTiming(WIDTHS[level], { duration: 200 });
    } else {
      progress.value = withTiming(0, { duration: 100 });
    }
  }, [shouldShow, level]);

  const animatedStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }));

  if (!shouldShow) {
    return null; // ❗ 少于6位不显示任何内容
  }

  return (
    <Container>
      <BarBackground>
        <AnimatedBar
          style={[
            animatedStyle,
            { backgroundColor: COLORS[level] },
          ]}
        />
      </BarBackground>
      <Desc>
        密码强度：<Label style={{ color: COLORS[level] }}>{LABELS[level]}</Label>
      </Desc>
      
    </Container>
  );
}

// ---------------- styled-components ----------------

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: 10px;
`;

const BarBackground = styled.View`
  flex: 1;
  height: 6px;
  background-color: #e3e6ec;
  border-radius: 4px;
  overflow: hidden;
`;

const AnimatedBar = styled(Animated.View)`
  height: 100%;
`;

const Desc = styled.Text`
  margin-left: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
`;

const Label = styled.Text`
  font-size: 12px;
`;

export default React.memo(PasswordStrengthAnimated);
